/**
 * Definições (em linguagem simples) dos indicadores do Dashboard de Clientes.
 * Fiel ao cálculo de `fv-api/dashboard/customer_calc.go`.
 *
 * Campos: `title` (nome exibido), `what` (o que mede), `how` (como é calculado),
 * `read` (como interpretar).
 *
 * Conceitos usados em vários textos:
 * - "compra" = venda Finalizada ou Ordem de Serviço Paga de cliente ativo;
 * - "inativo" = mais de 60 dias sem compra (ou sem compra e cadastrado há mais de 60 dias);
 * - o período anterior tem a mesma duração do período escolhido.
 */
export const CUSTOMER_INDICATOR_HELP = {
    base: {
        title: 'Clientes cadastrados',
        what: 'Quantos clientes ativos existem na sua base até o fim do período.',
        how: 'Conta todos os clientes ativos cadastrados até a data final do período (limitada a hoje). Abaixo, a base é dividida em ativos e inativos: inativo é quem está há mais de 60 dias sem uma compra (venda finalizada ou OS paga); clientes que nunca compraram contam desde a data de cadastro. Ativos = cadastrados − inativos.',
        read: 'Uma base que cresce é bom sinal, mas olhe também a proporção de inativos: muitos cadastros parados significam clientes que você já conquistou e deixou de vender. Aqui, subir o número de inativos é ruim.',
    },
    new: {
        title: 'Novos clientes',
        what: 'Quantos clientes foram cadastrados dentro do período.',
        how: 'Conta os clientes cujo cadastro caiu entre a data inicial e a final. O percentual mostrado é essa quantidade dividida pelo total de clientes cadastrados.',
        read: 'Mostra a capacidade de atrair clientes. Compare com o período anterior (seta ao lado do valor) para ver se a aquisição acelerou ou desacelerou.',
    },
    ticket: {
        title: 'Ticket médio por cliente',
        what: 'Quanto, em média, cada cliente que comprou gastou no período.',
        how: 'Valor total gerado no período (vendas + OS) dividido pelo número de clientes distintos que compraram. Quem não comprou não entra na conta.',
        read: 'Quanto maior, mais cada cliente deixa de receita. Se cair mesmo com mais clientes comprando, pode ser sinal de compras menores ou mais descontos.',
    },
    revenue: {
        title: 'Valor total gerado',
        what: 'Quanto os clientes identificados movimentaram de receita no período.',
        how: 'Soma do valor das vendas finalizadas e das ordens de serviço pagas de clientes cadastrados dentro do período. "Interações" é o número de vendas + OS somadas. Vendas sem cliente identificado não entram.',
        read: 'É o resultado financeiro da sua base no período. Compare com o período anterior para ver tendência.',
    },
    frequency: {
        title: 'Frequência média de retorno',
        what: 'De quanto em quanto tempo, em média, o cliente volta a comprar.',
        how: 'Para cada cliente, mede os dias entre uma compra e a seguinte (considerando as compras que ocorreram no período e a compra imediatamente anterior a elas) e tira a média de todos esses intervalos. Fica vazio se ninguém voltou a comprar.',
        read: 'Menor é melhor: clientes que voltam mais rápido são mais fiéis. Use para definir quando fazer contato de recompra.',
    },
    profile: {
        title: 'Perfil de clientes (PF / PJ)',
        what: 'Divisão da base entre Pessoa Física (CPF) e Pessoa Jurídica (CNPJ).',
        how: 'Percentual de clientes cadastrados como PF e como PJ, sobre o total de clientes cadastrados na data final do período.',
        read: 'Não é bom nem ruim: mostra quem é seu público. Ajuda a decidir o tipo de oferta e comunicação (ex.: condições para empresas).',
    },
    birthdays: {
        title: 'Aniversariantes no período',
        what: 'Quantos clientes fazem aniversário entre a data inicial e a final.',
        how: 'Conta os clientes com data de nascimento cadastrada cujo dia e mês caem dentro do período. Quem está sem data de nascimento não aparece.',
        read: 'É uma oportunidade de contato: uma mensagem de parabéns ou condição especial ajuda a aproximar e a reativar clientes.',
    },
    incomplete: {
        title: 'Cadastro incompleto',
        what: 'Clientes que não têm telefone e/ou e-mail cadastrado.',
        how: 'Conta quem está sem telefone (celular e fixo vazios), sem e-mail, ou sem os dois. A porcentagem é sobre o total de clientes cadastrados.',
        read: 'Menor é melhor. Sem contato não é possível enviar campanhas, lembretes ou mensagem de aniversário. Peça os dados no próximo atendimento.',
    },
    ltv: {
        title: 'LTV médio por cliente',
        what: 'Quanto, em média, um cliente já gerou de receita desde que começou a comprar (valor ao longo da vida do cliente).',
        how: 'Receita histórica acumulada (vendas + OS de todo o histórico, até o fim do período) dividida pelo número de clientes que já compraram alguma vez. Não depende apenas do período escolhido.',
        read: 'Maior é melhor. Serve de referência de quanto vale conquistar e manter um cliente, e quanto vale investir para trazê-lo de volta.',
    },
    recurrence: {
        title: 'Taxa de recorrência',
        what: 'Qual parcela dos clientes que compraram no período comprou mais de uma vez.',
        how: 'Clientes com 2 ou mais compras no período (vendas + OS) ÷ clientes que compraram no período, em porcentagem.',
        read: 'Maior é melhor: indica fidelidade. Taxa baixa significa que a maioria compra uma vez só e não volta.',
    },
    atRisk: {
        title: 'Em risco de inatividade',
        what: 'Clientes que estão esfriando: ainda não são inativos, mas já ficaram um tempo sem comprar.',
        how: 'Conta quem teve a última compra entre 30 e 60 dias antes da data de corte (fim do período, limitado a hoje). A porcentagem é sobre a base cadastrada.',
        read: 'Menor é melhor. É a hora certa de contatar esses clientes, antes de passarem de 60 dias e virarem inativos.',
    },
    reactivated: {
        title: 'Clientes reativados',
        what: 'Clientes que estavam inativos e voltaram a comprar.',
        how: 'Conta os clientes que tinham ficado 60 dias ou mais sem comprar e fizeram uma nova compra dentro do período.',
        read: 'Maior é melhor: mostra que suas ações de retomada de contato estão funcionando.',
    },
    concentration: {
        title: 'Concentração (top 10)',
        what: 'O quanto da sua receita depende dos 10 maiores clientes.',
        how: 'Soma do que os 10 clientes que mais gastaram no período pagaram ÷ valor total gerado no período, em porcentagem.',
        read: 'Menor é mais seguro. Valor alto significa dependência: perder um desses clientes causaria uma queda grande no faturamento.',
    },
    growthChart: {
        title: 'Crescimento da base de clientes',
        what: 'Evolução do total de clientes cadastrados ao longo do período.',
        how: 'Cada ponto é o total acumulado de clientes ativos cadastrados até aquele dia, semana ou mês (a escala muda conforme o tamanho do período).',
        read: 'Linha subindo = base crescendo. Linha reta = poucos cadastros novos.',
    },
    flowChart: {
        title: 'Novos x Inativados x Reativados',
        what: 'O movimento da base em cada dia, semana ou mês: quem entrou, quem esfriou e quem voltou.',
        how: 'Novos = cadastros do período. Inativados = clientes que completaram 60 dias sem comprar naquele intervalo. Reativados = inativos que voltaram a comprar.',
        read: 'O ideal é Novos e Reativados acima de Inativados. Se os inativados dominam, a base está perdendo clientes mais rápido do que ganha.',
    },
    spendBands: {
        title: 'Clientes por faixa de gasto',
        what: 'Quantos clientes se encaixam em cada faixa de valor gasto no período.',
        how: 'Considera só os clientes que compraram no período, soma o que cada um gastou (vendas + OS) e os agrupa nas faixas de valor exibidas. A porcentagem é sobre os clientes que compraram.',
        read: 'Mostra se a receita vem de muitos clientes pequenos ou de poucos grandes. Ajuda a criar ofertas para levar clientes de uma faixa para a seguinte.',
    },
    topValue: {
        title: 'Maior valor gasto',
        what: 'Os 5 clientes que mais gastaram no período.',
        how: 'Ranking pela soma de vendas finalizadas + OS pagas no período. A porcentagem abaixo do valor é a fatia desse cliente no total gerado.',
        read: 'São seus clientes mais valiosos: vale cuidar bem deles. Participações altas indicam dependência.',
    },
    topFrequency: {
        title: 'Maior frequência',
        what: 'Os 5 clientes que mais compraram no período.',
        how: 'Ranking pelo número de vendas finalizadas + OS pagas no período. Abaixo, o valor total que cada um gerou.',
        read: 'São seus clientes mais fiéis, mesmo que nem sempre os que mais gastam. Bons candidatos a programas de fidelidade.',
    },
    birthdayList: {
        title: 'Aniversariantes do período',
        what: 'Lista dos próximos clientes a fazer aniversário no período.',
        how: 'Mostra até 5 clientes, começando por quem faz aniversário hoje ou em seguida. A etiqueta "inativo" aponta quem também está há mais de 60 dias sem comprar.',
        read: 'Use como roteiro de contato: parabenizar um cliente inativo é uma ótima chance de trazê-lo de volta.',
    },
    inactiveList: {
        title: 'Clientes inativos',
        what: 'Clientes que estão há mais de 60 dias sem comprar.',
        how: 'Lista (até 5) dos clientes inativos, dos que gastaram mais ao longo da história para os que gastaram menos. Mostra a data da última interação (ou cadastro, se nunca comprou), os dias parado e o total já gasto. A faixa vermelha indica mais de 90 dias.',
        read: 'Comece por quem tem maior valor histórico: é o retorno mais provável e mais relevante para a empresa.',
    },
};

export const CUSTOMER_GLOSSARY_ORDER = [
    'base', 'new', 'ticket', 'revenue', 'frequency',
    'profile', 'birthdays', 'incomplete', 'ltv',
    'recurrence', 'atRisk', 'reactivated', 'concentration',
    'growthChart', 'flowChart', 'spendBands', 'topValue', 'topFrequency',
    'birthdayList', 'inactiveList',
];
